// This module is the reason for the  no-restricted-imports rule, so we need to ignore it only in this one file
/* oxlint-disable no-restricted-imports */
/* oxlint-disable quantic/no-type-exports */

/**
 * This module "FrontRoyalReactHookForm" is a re-export of the standard "react-hook-form" library, except
 * that we decided we want to change the default options for the "setValue" function that exists on the
 * useForm and useFormContext hooks. We have decided that we want the shouldValidate and shouldDirty options to default to true.
 * Without this module we would need to pass { shouldValidate: true, shouldDirty: true } in every setValue call from these two hooks.
 *
 * We have kept the original api and ability to overwrite these changes the same, but for clarity, we renamed the function from
 * "setValue" to "setFieldValue"
 *
 * @example if you did need to overwrite our updated settings
 *
 *  1. const { setFieldValue } = useFormContext();
 *  2. setFieldValue('nameOfFormField', newValue, { shouldValidate: false, shouldDirty: false })
 *
 */
import { useCallback, type JSX } from 'react';
import {
    useForm as useRHFForm,
    useFormContext as useRHFFormContext,
    FormProvider as RHHFFormProvider,
    type FieldValues,
    type Path,
    type Resolver,
    type UseFormProps,
    type UseFormReturn as RHFUseFormReturn,
    type FormProviderProps as RHHFFormProviderProps,
} from 'react-hook-form';
import { yupResolver as rhfYupResolver } from '@hookform/resolvers/yup';
import { type AnyObjectSchema } from 'yup';

export * from 'react-hook-form';

/**
 * Re-typed wrapper around `@hookform/resolvers`' yupResolver.
 *
 * As of `@hookform/resolvers` v5, `yupResolver` returns `Resolver<Input, Context, InferType>`, where
 * `Input` reflects the schema's *validated* shape (required fields) while a form typed with
 * `Yup.InferType<typeof schema>` treats those same fields as optional. That difference makes the
 * resolver fail to assign to `useForm`'s resolver slot. Since our forms type `useForm` from
 * `InferType`, we coerce the resolver to `Resolver<TFieldValues>`, which `useForm<TFieldValues>`
 * infers from context. Runtime behavior is unchanged — this is purely a type alignment.
 */
export function yupResolver<TFieldValues extends FieldValues = FieldValues>(
    schema: AnyObjectSchema,
    schemaOptions?: Parameters<AnyObjectSchema['validate']>[1],
    resolverOptions?: { mode?: 'async' | 'sync'; raw?: false },
): Resolver<TFieldValues> {
    return rhfYupResolver(schema, schemaOptions, resolverOptions) as unknown as Resolver<TFieldValues>;
}

export type UseFormReturn<TFieldValues extends FieldValues = FieldValues, TContext = unknown> = Omit<
    RHFUseFormReturn<TFieldValues, TContext>,
    'setValue'
> & { setFieldValue: RHFUseFormReturn<TFieldValues, TContext>['setValue'] };

// `FormProvider` only forwards RHF's own methods into context and drops extras like `setFieldValue`
const useSetFieldValue = <TFieldValues extends FieldValues>(
    setValue: RHFUseFormReturn<TFieldValues>['setValue'],
): RHFUseFormReturn<TFieldValues>['setValue'] =>
    useCallback(
        (
            name: Path<TFieldValues>,
            value: TFieldValues[typeof name],
            options = { shouldValidate: true, shouldDirty: true, shouldTouch: true },
        ) => setValue(name, value, options),
        [setValue],
    );

export const useForm = <TFieldValues extends FieldValues = FieldValues, TContext = unknown>(
    props?: UseFormProps<TFieldValues, TContext>,
): UseFormReturn<TFieldValues, TContext> => {
    const methods = useRHFForm<TFieldValues>(props);
    const setFieldValue = useSetFieldValue<TFieldValues>(methods.setValue);

    return { ...methods, setFieldValue };
};

export const useFormContext = <TFieldValues extends FieldValues = FieldValues>(): UseFormReturn<TFieldValues> => {
    const methods = useRHFFormContext<TFieldValues>();
    const setFieldValue = useSetFieldValue<TFieldValues>(methods.setValue);

    return { ...methods, setFieldValue };
};

export type FormProviderProps<TFieldValues extends FieldValues = FieldValues, TContext = unknown> = Omit<
    RHHFFormProviderProps<TFieldValues, TContext>,
    'setValue'
> & { setFieldValue: RHFUseFormReturn<TFieldValues, TContext>['setValue'] };

export const FormProvider = RHHFFormProvider as unknown as <TFieldValues extends FieldValues, TContext = unknown>(
    props: FormProviderProps<TFieldValues, TContext>,
) => JSX.Element;
